import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Wine } from "lucide-react";

const sectionLinks = [
  { label: "About", id: "about" },
  { label: "Packages", id: "packages" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

const pageLinks = [
  { label: "Quote", to: "/quote" },
  { label: "Ask AI", to: "/assistant" },
  { label: "Cocktails", to: "/cocktails" },
];

export const Nav = ({ solid = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolledState, setScrolledState] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const scrolled = solid || scrolledState;

  useEffect(() => {
    const onScroll = () => setScrolledState(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl shadow-[0_4px_30px_rgba(10,31,45,0.08)] py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span
            aria-hidden="true"
            className={`w-11 h-11 rounded-full shrink-0 bg-cream ring-1 transition-all ${scrolled ? "ring-sand" : "ring-white/40"}`}
            style={{ backgroundImage: "url(/logo.png)", backgroundSize: "300%", backgroundPosition: "center 18%" }}
          />
          <span className={`font-serif text-xl md:text-2xl leading-none tracking-tight transition-colors ${scrolled ? "text-ocean-deep" : "text-white"}`}>
            Beachside Bartending
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {sectionLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => goSection(l.id)}
              className={`overline text-xs link-underline transition-colors ${scrolled ? "text-ocean hover:text-coral" : "text-white/90 hover:text-white"}`}
            >
              {l.label}
            </button>
          ))}
          <span className={`h-4 w-px ${scrolled ? "bg-sand" : "bg-white/30"}`} />
          {pageLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-page-${l.label.toLowerCase().replace(" ", "-")}`}
              className={`overline text-xs link-underline transition-colors ${scrolled ? "text-ocean hover:text-coral" : "text-white/90 hover:text-white"}`}
            >
              {l.label}
            </Link>
          ))}
          <button
            data-testid="nav-book-btn"
            onClick={() => goSection("contact")}
            className="rounded-full bg-coral px-5 py-2.5 text-white text-xs overline hover:bg-[#c9633f] transition-colors"
          >
            Book Now
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className={`lg:hidden ${scrolled ? "text-ocean-deep" : "text-white"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-cream/95 backdrop-blur-xl border-t border-sand mt-3 px-6 py-6 flex flex-col gap-4">
          {sectionLinks.map((l) => (
            <button key={l.id} onClick={() => goSection(l.id)} className="text-left overline text-sm text-ocean">
              {l.label}
            </button>
          ))}
          <div className="h-px bg-sand my-1" />
          {pageLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="overline text-sm text-coral">
              {l.label}
            </Link>
          ))}
          <button onClick={() => goSection("contact")} className="rounded-full bg-coral px-5 py-3 text-white text-sm overline mt-2">
            Book Now
          </button>
        </div>
      )}
    </header>
  );
};

export default Nav;
